import React from "react";
import {Text} from "@gs-ux-uitoolkit-react/text";
import {Footer} from "../../../shared/components/Footer";
import {Header} from "../../../shared/components/Header";
import {MaintenanceMessage} from "./MaintenanceMessage";
import {QuoteMediaDisclosure} from "./QuoteMediaDisclosure";
import backgroundGscs from "../../../assets/background_gscs.png";
import gsCustodySolutionsLogoBlack from "../../../assets/gsCustodySolutionsLogoBlack.svg";
import privacyNotice from "../../../assets/privacyNotice.pdf";

interface SignInPageLayoutProps {
    /** Content rendered inside the white sign-in box, below the maintenance messages. */
    children: React.ReactNode;
    /** Extra slot rendered between the layout (e.g. error/success banners) and the children. */
    topSlot?: React.ReactNode;
    /** Extra slot rendered below the box (e.g. SignInAgreement, QuoteMediaDisclosure). */
    footerSlot?: React.ReactNode;
    /**
     * Whether to show the global Header. The figma design embeds the brand
     * header inside individual forms, so callers can opt out.
     */
    showHeader?: boolean;
    /**
     * Whether to show the maintenance messages inside the sign-in box.
     * Defaults to true.
     */
    showMaintenanceMessage?: boolean;
    /**
     * Whether to show the "By signing in..." agreement text and the
     * QuoteMedia disclosure inside the sign-in box. Defaults to false; only
     * the Sign-In page (username/password) renders these.
     */
    showAgreement?: boolean;
}

/**
 * Shared page layout for the sign-in family of pages.
 *
 * Responsibilities:
 *  - Applies the GSCS background image across every page.
 *  - Centers a white "sign-in box" with a fixed minimum height, derived from
 *    the Figma reference (Frame 2049771990) so the layout does not jump
 *    between username / password / MFA / forgot-password / maintenance states.
 *  - Renders maintenance messages inside the sign-in box (per Figma design)
 *    so they are visually part of the same surface as the form.
 */
export function SignInPageLayout({
    children,
    topSlot,
    footerSlot,
    showHeader = false,
    showMaintenanceMessage = true,
    showAgreement = false,
}: SignInPageLayoutProps) {
    return (
        <main
            className="flex flex-col min-h-[100vh]"
            style={{
                backgroundImage: `url(${backgroundGscs})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed',
            }}
            data-qa-comp="sign-in-page-layout"
        >
            {showHeader && <Header/>}
            {/*
             * Card container — top-anchored (items-start) per the redesign so the
             * card sits near the top of the viewport rather than vertically centered.
             * px=24, pt=48 matches the reference's `cardContainer` padding.
             */}
            <div className="flex-1 flex flex-col items-center px-6 pt-12 pb-[124px]">
                <div
                    className="w-full max-w-[520px] min-h-[524px] bg-white shadow-lg flex flex-col items-stretch p-8 gap-6"
                    data-qa-comp="sign-in-box"
                >
                    {/* Header bar: logo (left) + "Interested in learning more about GSCS?" CTA (right) */}
                    <div
                        className="flex flex-row items-center justify-between gap-4"
                        data-qa-comp="sign-in-box-header"
                    >
                        <a
                            href="/sign-in"
                            aria-label="Goldman Sachs Custody Solutions - back to sign in"
                            data-qa-comp="logo-link"
                        >
                            <img
                                className="w-[160px]"
                                src={gsCustodySolutionsLogoBlack}
                                alt="Logo - Goldman Sachs Custody Solutions"
                                title="Logo - Goldman Sachs Custody Solutions"
                                data-qa-comp="logo"
                            />
                        </a>
                        <div className="flex flex-col items-end text-right flex-shrink-0">
                            <Text typography="textBody02Regular" style={{ color: 'rgba(0, 0, 0, 0.8)', textAlign: 'right', fontSize: '0.8125rem' }} className="whitespace-nowrap">
                                Interested in learning more about GSCS?
                            </Text>
                            <a
                                href="https://www.goldmansachs.com/what-we-do/FICC-and-equities/goldman-sachs-advisor-solutions/contact_us.html"
                                target="_blank"
                                style={{ color: 'rgb(68, 110, 166)' }}
                                aria-label="Opens in New Tab - Click here"
                                data-qa-comp="get-started-link"
                                className="text-sm hover:underline text-right"
                            >
                                Click here
                            </a>
                        </div>
                    </div>

                    {topSlot}

                    {/*
                     * Form / page content. Top-anchored so switching between
                     * short forms (password, MFA) and tall forms (username,
                     * forgot password) does not cause the card height to jump.
                     */}
                    <div className="flex flex-col items-stretch" data-qa-comp="sign-in-box-content">
                        {children}
                    </div>

                    {showMaintenanceMessage && (
                        <div data-qa-comp="sign-in-box-maintenance">
                            <MaintenanceMessage/>
                        </div>
                    )}

                    {showAgreement && (
                        <div className="flex flex-col items-stretch gap-4" data-qa-comp="sign-in-box-agreement">
                            <Text typography="textBody02Regular" style={{ color: 'rgb(28, 43, 54)' }}>
                                By signing in you agree to any changes in your{' '}
                                <a
                                    href={`${import.meta.env.BASE_URL}/customerAgreement.pdf`}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ color: 'rgb(68, 110, 166)' }}
                                    className="hover:underline"
                                >
                                    Customer Agreement
                                </a>
                                {' '}and acknowledge receipt of our{' '}
                                <a
                                    href={privacyNotice}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ color: 'rgb(68, 110, 166)' }}
                                    className="hover:underline"
                                >
                                    Privacy Notice
                                </a>
                                .
                            </Text>
                            {/*
                             * Divider is rendered by QuoteMediaDisclosure itself
                             * (border-t + pt/mt) so we don't add another one here.
                             */}
                            <QuoteMediaDisclosure/>
                        </div>
                    )}
                </div>
                {footerSlot}
            </div>
            <Footer/>
        </main>
    );
}
